import { computed, ref, Ref } from 'vue';
import { ActiveModalActAccessStates, ModalActAccessStates } from '@/models/modal/modal.enum';
import { ActAddedUserTypes, IActUserToAdd, IDashboardAccessPeriodsValidationIssue } from '@/models/act/act-access.interface';
import { ProjectProduct } from '@studyportals/crm-data-processor-interface';
import * as Ladda from 'ladda';
import actPresenter from '@/presenters/act-presenter';
import actStore from '@/store/act';

export default class ActNavigationCode {
	public confirmButton: Ref<HTMLButtonElement | null> = ref(null);

	public isPreviousStepPossible = computed((): boolean => {
		return this.modalActAccessState.value > ModalActAccessStates.ADD_USERS;
	});

	public isNextStepPossible = computed((): boolean => {
		return !this.isStepAddUsersAndInvalid.value
			&& !this.isStepSelectDashboardsAndInvalid.value
			&& !this.isStepSetAccessPeriodsAndInvalid.value;
	});

	public isLastStepReached = computed((): boolean => {
		return this.modalActAccessState.value >= ModalActAccessStates.REVIEW_AND_CONFIRM;
	});

	public modalActAccessState = computed((): ActiveModalActAccessStates => {
		return actStore.actGetters.modalActAccessState() as ActiveModalActAccessStates;
	});

	private isStepAddUsersAndInvalid = computed((): boolean => {
		return this.modalActAccessState.value === ModalActAccessStates.ADD_USERS
			&& (this.areSomeActUsersToAddInvalid.value || !this.actSomeUsersToAddValid.value);
	});

	private isStepSelectDashboardsAndInvalid = computed((): boolean => {
		return this.modalActAccessState.value === ModalActAccessStates.SELECT_DASHBOARDS
			&& !this.actDashboardsToAdd.value.length;
	});

	private isStepSetAccessPeriodsAndInvalid = computed((): boolean => {
		return this.modalActAccessState.value === ModalActAccessStates.SET_ACCESS_PERIODS
			&& this.dashboardAccessPeriodsValidationIssues.value.length !== 0;
	});

	private areSomeActUsersToAddInvalid = computed((): boolean => {
		const invalidUsers = this.actUsersToAdd.value
			.filter(user => user.type !== ActAddedUserTypes.VALID);
		return invalidUsers.length > 0;
	});

	private actSomeUsersToAddValid = computed((): boolean => {
		const validUsers = this.actUsersToAdd.value
			.filter(user => user.type === ActAddedUserTypes.VALID);
		return validUsers.length > 0;
	});

	public dashboardAccessPeriodsValidationIssues = computed((): IDashboardAccessPeriodsValidationIssue[] => {
		return actPresenter.dashboardAccessPeriodsValidationIssues.value;
	});

	private actUsersToAdd = computed((): IActUserToAdd[] => {
		return actStore.actGetters.actUsersToAdd();
	});

	private actDashboardsToAdd = computed((): ProjectProduct[] => {
		return actStore.actGetters.actDashboardsToAdd();
	});

	public handleBackClick(): void {
		actStore.actMutations.setModalActAccessState(this.modalActAccessState.value - 1);
	}

	public async handleNextClick(): Promise<void> {
		if (!this.isLastStepReached.value) {
			actStore.actMutations.setModalActAccessState(this.modalActAccessState.value + 1);
			return;
		}

		this.triggerLoadingAnimation();
		
		await actPresenter.saveActEntries();

		actStore.actMutations.setModalActAccessState(ModalActAccessStates.INACTIVE);
	}
	
	private triggerLoadingAnimation(): void {
		if (!this.confirmButton.value) {
			return;
		}

		Ladda.create(this.confirmButton.value).start();
	}
}
